import React, { useState } from 'react';
import axios from 'axios';
import { Lightbulb, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { value: 'event_idea', label: 'Event Idea' },
  { value: 'community_outreach', label: 'Community Outreach' },
  { value: 'food_assistance', label: 'Food Assistance' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'volunteer_idea', label: 'Volunteer Idea' },
  { value: 'general_feedback', label: 'General Feedback' },
];

/**
 * Public "Help Us Grow With The Community" suggestions form.
 *
 * Lives on the homepage between the testimonials/share-story section and the
 * footer. Submits to POST /api/public/suggestions. Server-side honeypot field
 * (`website`) is rendered hidden to deter bots.
 */
const CommunitySuggestionsSection = () => {
  const [form, setForm] = useState({
    name: '', email: '', suggestion_type: '', message: '', website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.suggestion_type) { setError('Please choose a suggestion type'); return; }
    if (form.message.trim().length < 10) { setError('Message must be at least 10 characters'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/suggestions`, {
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        suggestion_type: form.suggestion_type,
        message: form.message.trim(),
        website: form.website, // honeypot
      });
      setSubmitted(true);
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Could not submit — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setForm({ name: '', email: '', suggestion_type: '', message: '', website: '' });
  };

  return (
    <section className="py-14 bg-white" data-testid="community-suggestions-section">
      <div className="px-6 max-w-2xl mx-auto">
        <div className="text-center mb-7">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#FEE2E2] flex items-center justify-center">
            <Lightbulb className="w-6 h-6 text-[#BA1826]" />
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2.5">
            Help Us Grow With The Community
          </h2>
          <p className="text-sm text-[#4B5563] leading-relaxed max-w-xl mx-auto">
            We believe the best ideas come from the people we serve and support. Share suggestions, outreach ideas, event ideas, partnerships, or ways we can better support the community.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white border-2 border-green-200 rounded-lg p-7 text-center shadow-sm" data-testid="suggestion-success">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-7 h-7 text-green-700" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">Thank you for sharing!</h3>
            <p className="text-sm text-[#4B5563] max-w-md mx-auto">
              Your idea has been received and will be reviewed by our team. Ideas like yours help shape what Novena does next — we appreciate you taking the time.
            </p>
            <button onClick={reset} className="mt-5 text-sm font-semibold text-[#BA1826] hover:underline" data-testid="suggestion-reset-btn">
              Submit another suggestion
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="bg-white border border-[#E5E7EB] rounded-lg p-6 sm:p-7 shadow-sm space-y-4" data-testid="suggestion-form">
            {/* Honeypot — hidden from real users */}
            <input
              type="text" name="website" tabIndex={-1} autoComplete="off"
              aria-hidden="true" value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none' }}
            />

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Name <span className="text-[#9CA3AF] font-normal normal-case tracking-normal">(optional)</span></label>
                <input
                  type="text" value={form.name} maxLength={120}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none focus:ring-1 focus:ring-[#BA1826]/30"
                  placeholder="Your name" data-testid="suggestion-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Email <span className="text-[#9CA3AF] font-normal normal-case tracking-normal">(optional)</span></label>
                <input
                  type="email" value={form.email} maxLength={200}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none focus:ring-1 focus:ring-[#BA1826]/30"
                  placeholder="email@example.com" data-testid="suggestion-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Suggestion Type <span className="text-red-600">*</span></label>
              <select
                value={form.suggestion_type}
                onChange={(e) => setForm({ ...form, suggestion_type: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-sm text-sm text-[#0A0A0A] bg-white focus:border-[#BA1826] focus:outline-none focus:ring-1 focus:ring-[#BA1826]/30"
                data-testid="suggestion-type-select"
              >
                <option value="">— Choose one —</option>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1">Your message <span className="text-red-600">*</span></label>
              <textarea
                value={form.message} rows={5} maxLength={5000}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none focus:ring-1 focus:ring-[#BA1826]/30 resize-none"
                placeholder="Tell us what you're thinking — an event idea, a community need, a partnership opportunity, or how we can serve better..."
                data-testid="suggestion-message-input"
              />
              <p className="text-[10px] text-[#9CA3AF] mt-1">{form.message.length} / 5000 characters</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm flex items-center gap-2" data-testid="suggestion-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            <button
              type="submit" disabled={submitting}
              className="w-full btn-primary py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="suggestion-submit-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> :
                <><Send className="w-4 h-4" /> Share Your Idea</>}
            </button>
            <p className="text-[11px] text-[#9CA3AF] text-center">
              We read every submission. Including your email is optional but helps us follow up if needed.
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export default CommunitySuggestionsSection;
